// src/pages/Courses/CourseDetail.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ICourse } from "./CoursesPage";
import LoginModal    from "../../components/Auth/LoginModel";
import TestPriceModal from "../../components/Modals/TestPricingModal";
import "./CourseDetails.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api/v1";

/* ── Load progress from localStorage (same key as LessonPlayer) ── */
function loadLocalProgress(slug: string): Set<number> {
  try {
    const raw = localStorage.getItem(`acd_progress_${slug}`);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as number[]);
  } catch { return new Set(); }
}

/* ══════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════ */
const IcoStar     = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>);
const IcoUsers    = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>);
const IcoClock    = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const IcoCheck    = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>);
const IcoVideo    = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="15" height="14" rx="2"/><path d="M17 9.5l5-3.5v13l-5-3.5"/></svg>);
//const IcoAward    = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>);
//const IcoInfinity = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12c-2-2.5-4-4-6-4a4 4 0 0 0 0 8c2 0 4-1.5 6-4z"/><path d="M12 12c2 2.5 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.5-6 4z"/></svg>);
const IcoChevron  = ({ open }: { open: boolean }) => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.25s" }}><polyline points="6 9 12 15 18 9"/></svg>);
const IcoPlay     = () => (<svg width="48" height="48" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="rgba(255,255,255,0.92)"/><polygon points="10 8 16 12 10 16 10 8" fill="#0d9e8a"/></svg>);
const IcoLive     = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="2" y="7" width="20" height="15" rx="2"/><polyline points="17 2 12 7 7 2" stroke="currentColor" fill="none" strokeWidth="2"/></svg>);
const IcoRecord   = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4" fill="white"/></svg>);
const IcoShield   = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>);
const IcoPlayFill = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>);
const IcoLock     = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>);
const IcoCheckDone= () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcoCertificate = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>);
const IcoDownload = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);
const IcoZap      = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>);

// const ICON_MAP: Record<string, React.ReactNode> = {
//   video:    <IcoVideo />,
//   clock:    <IcoClock />,
//   award:    <IcoAward />,
//   infinity: <IcoInfinity />,
// };

/* Skeleton loader */
const DetailSkeleton = () => (
  <div className="cd-page">
    <div className="cd-back-bar"><div className="cd-skel-line" style={{ width:120, height:16 }} /></div>
    <div className="cd-layout">
      <div className="cd-main">
        <div className="cd-skel-hero" />
        <div className="cd-skel-card" />
        <div className="cd-skel-card" />
      </div>
      <aside className="cd-sidebar"><div className="cd-skel-sidebar" /></aside>
    </div>
  </div>
);

/* ══════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════ */
const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();   // courseId = slug
  const navigate     = useNavigate();

  /* ── Data state ── */
  const [course,           setCourse]           = useState<ICourse | null>(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState("");
  const [openModule,       setOpenModule]       = useState<number | null>(0);
  const [isEnrolled,       setIsEnrolled]       = useState(false);
  const [completedSet,     setCompletedSet]     = useState<Set<number>>(new Set());

  /* ── Modal state — same pattern as TestSeriesNormal ── */
  const [showLoginModal,   setShowLoginModal]   = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  /* ── Fetch course detail ── */
  useEffect(() => {
    if (!courseId) return;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/courses/${courseId}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setCourse(d.data);
          setCompletedSet(loadLocalProgress(d.data.slug));
        } else {
          setError("Course not found");
        }
      })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  }, [courseId]);

  /* ── Check payment status (same pattern as TestSeries) ── */
  useEffect(() => {
    if (!courseId) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/courses/${courseId}/payment-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (d.success && d.data.paid) setIsEnrolled(true); })
      .catch(() => {});
  }, [courseId]);

  /* ── Derived values ── */
  const savePct = course
    ? Math.round((1 - course.price / course.original_price) * 100)
    : 0;

  const allModuleLessons = useMemo(() => {
    if (!course) return [];
    let globalIdx = 0;
    return course.modules.map(mod => ({
      ...mod,
      lessonsWithIdx: mod.lessons.map(lesson => ({
        ...lesson,
        globalIdx: globalIdx++,
      })),
    }));
  }, [course]);

  const totalLessons    = useMemo(() => allModuleLessons.reduce((s, m) => s + m.lessons.length, 0), [allModuleLessons]);
  const completedCount  = completedSet.size;
  const progressPct     = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  /* ── After login success — re-check payment ── */
  const handleLoginSuccess = () => {
    if (!courseId) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    fetch(`${API_BASE}/courses/${courseId}/payment-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data.paid) {
          setIsEnrolled(true);
        } else {
          // logged in but not paid → open pricing modal
          setShowPricingModal(true);
        }
      })
      .catch(() => {});
  };

  /* ── Enroll button — same logic as TestSeries handleUnlockClick ── */
  const handleEnrollClick = () => {
    if (isEnrolled) {
      navigate(`/courses/${course!.slug}/learn`);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      // NOT logged in → open LoginModal
      localStorage.setItem("postLoginModelRedirect", window.location.pathname);
      setShowLoginModal(true);
    } else {
      // Logged in but not paid → open pricing modal
      setShowPricingModal(true);
    }
  };

  /* ── Lesson click ── */
  const handleLessonClick = (globalIdx: number, isPreview: boolean) => {
    const token = localStorage.getItem("token");

    if (isEnrolled) {
      // Paid → go straight to lesson
      navigate(`/courses/${course!.slug}/learn/${globalIdx}`);
      return;
    }

    if (isPreview) {
      if (!token) {
        // Preview + not logged in → open login modal first
        localStorage.setItem("postLoginModelRedirect", window.location.pathname);
        setShowLoginModal(true);
      } else {
        // Preview + logged in → play directly
        navigate(`/courses/${course!.slug}/learn/${globalIdx}`);
      }
      return;
    }

    // Locked lesson
    if (!token) {
      localStorage.setItem("postLoginModelRedirect", window.location.pathname);
      setShowLoginModal(true);
    } else {
      setShowPricingModal(true);
    }
  };

  /* ── Loading / Error states ── */
  if (loading) return <DetailSkeleton />;
  if (error || !course) return (
    <div style={{ textAlign:"center", padding:80 }}>
      <h2>{error || "Course not found"}</h2>
      <Link to="/courses">← Back to Courses</Link>
    </div>
  );

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <>
      <Helmet>
        <title>{course.title} – Acadebility</title>
        <meta name="description" content={course.description} />
      </Helmet>

      <div className="cd-page">

        {/* Back bar */}
        <div className="cd-back-bar">
          <Link to="/courses" className="cd-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Courses
          </Link>
        </div>

        <div className="cd-layout">

          {/* ════ LEFT MAIN ════ */}
          <div className="cd-main">

            {/* Hero card */}
            <div className="cd-hero-card">
              <div className="cd-hero-badges">
                {course.is_bestseller && <span className="cd-badge cd-badge-bestseller">🏆 Bestseller</span>}
                {course.is_live       && <span className="cd-badge cd-badge-live"><IcoLive /> Live Classes</span>}
                {course.is_recorded   && <span className="cd-badge cd-badge-recorded"><IcoRecord /> Recorded</span>}
                <span className="cd-badge cd-badge-cat">{course.category}</span>
              </div>

              <h1 className="cd-hero-title">{course.title}</h1>
              <p  className="cd-hero-desc">{course.description}</p>

              <div className="cd-hero-meta">
                <span className="cd-meta-item cd-meta-rating">
                  <IcoStar />
                  <strong>{course.rating}</strong>
                  <span className="cd-meta-rating-count">({course.review_count.toLocaleString("en-IN")} ratings)</span>
                </span>
                <span className="cd-meta-item"><IcoUsers /> {course.students_display} students</span>
                <span className="cd-meta-item"><IcoClock /> {course.duration_display}</span>
              </div>

              <div className="cd-divider" />

              <div className="cd-instructor-label">Instructor</div>
              <div className="cd-instructor-row">
                <div className="cd-instructor-avatar">{course.instructor_avatar}</div>
                <div>
                  <div className="cd-instructor-name">{course.instructor}</div>
                  <div className="cd-instructor-title">{course.instructor_title}</div>
                </div>
              </div>
            </div>

            {/* What You'll Learn */}
            <div className="cd-section-card">
              <h2 className="cd-section-title">What You'll Learn</h2>
              <div className="cd-learn-grid">
                {course.what_you_learn.map(item => (
                  <div className="cd-learn-item" key={item}>
                    <span className="cd-learn-icon"><IcoCheck /></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Course Content */}
            <div className="cd-section-card">
              <div className="cd-content-head">
                <h2 className="cd-section-title">Course Content</h2>
                <span className="cd-content-meta">
                  {course.modules.length} modules · {totalLessons} lessons
                </span>
              </div>

              {/* Progress bar — shown only if user has started */}
              {completedCount > 0 && (
                <div className="cd-progress-wrap">
                  <div className="cd-progress-label-row">
                    <span className="cd-progress-label">Your Progress</span>
                    <span className="cd-progress-pct">{progressPct}%</span>
                  </div>
                  <div className="cd-progress-track">
                    <div className="cd-progress-fill" style={{ width:`${progressPct}%` }} />
                  </div>
                  <div className="cd-progress-sub">{completedCount} of {totalLessons} lessons completed</div>
                </div>
              )}

              <div className="cd-modules">
                {allModuleLessons.map((mod, mi) => (
                  <div className={`cd-module${openModule === mi ? " cd-module-open" : ""}`} key={mod.module_id}>
                    <button className="cd-module-header" onClick={() => setOpenModule(openModule === mi ? null : mi)}>
                      <div className="cd-module-num">{mod.module_id}</div>
                      <div className="cd-module-info">
                        <div className="cd-module-title">{mod.title}</div>
                        <div className="cd-module-meta">
                          {mod.lessons.length} lessons
                          {mod.lessons.reduce((s, l) => s + (l.duration_sec || 0), 0) > 0 &&
                            ` · ${Math.ceil(mod.lessons.reduce((s, l) => s + (l.duration_sec || 0), 0) / 3600)} hrs`}
                        </div>
                      </div>
                      <IcoChevron open={openModule === mi} />
                    </button>

                    {openModule === mi && (
                      <div className="cd-module-lessons">
                        {mod.lessonsWithIdx.map(lesson => {
                          const isDone    = completedSet.has(lesson.globalIdx);
                          const isPreview = lesson.is_preview;
                          const token     = localStorage.getItem("token");
                          /* canAccess: enrolled, or it's a preview AND logged in */
                          const canAccess = isEnrolled || (isPreview && !!token);

                          return (
                            <div
                              key={lesson.lesson_id}
                              className={`cd-lesson${isDone ? " cd-lesson-done" : ""}${!isEnrolled && !isPreview ? " cd-lesson-locked" : ""}`}
                              onClick={() => handleLessonClick(lesson.globalIdx, isPreview)}
                            >
                              {/* Icon */}
                              <div className={`cd-lesson-icon${isDone ? " done" : ""}`}>
                                {isDone
                                  ? <IcoCheckDone />
                                  : canAccess ? <IcoPlayFill /> : <IcoLock />
                                }
                              </div>

                              {/* Title + badges */}
                              <div className="cd-lesson-info">
                                <span className="cd-lesson-title">{lesson.title}</span>
                                <div className="cd-lesson-badges">
                                  {isPreview && !isEnrolled && (
                                    <span className="cd-lesson-preview">👁️ Free Preview</span>
                                  )}
                                  {isDone && (
                                    <span className="cd-lesson-completed-badge">✓ Done</span>
                                  )}
                                </div>
                              </div>

                              {/* Duration */}
                              {lesson.duration_sec > 0 && (
                                <span className="cd-lesson-dur">
                                  {Math.floor(lesson.duration_sec / 60)}:{String(lesson.duration_sec % 60).padStart(2,"0")}
                                </span>
                              )}

                              {/* Right state indicator */}
                              {!isEnrolled && !isPreview && (
                                <span className="cd-lesson-lock-icon"><IcoLock /></span>
                              )}
                              {!isEnrolled && isPreview && (
                                <span className="cd-lesson-play-btn">▶</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Instructor bio card */}
            <div className="cd-section-card">
              <h2 className="cd-section-title">About the Instructor</h2>
              <div className="cd-instr-bio-row">
                <div className="cd-instr-bio-avatar">{course.instructor_avatar}</div>
                <div>
                  <div className="cd-instr-bio-name">{course.instructor}</div>
                  <div className="cd-instr-bio-title">{course.instructor_title}</div>
                  <p className="cd-instr-bio-text">{course.instructor_bio}</p>
                </div>
              </div>
            </div>
          </div>

          {/* ════ RIGHT SIDEBAR ════ */}
          <aside className="cd-sidebar">
            <div className="cd-enroll-card">

              {/* Preview thumbnail */}
              <div
                className="cd-preview-wrap"
                onClick={() => {
                  const token = localStorage.getItem("token");
                  if (!token) {
                    localStorage.setItem("postLoginModelRedirect", window.location.pathname);
                    setShowLoginModal(true);
                  } else {
                    navigate(`/courses/${course.slug}/learn/0`);
                  }
                }}
              >
                <img src={course.image_url} alt={course.title} className="cd-preview-img" />
                <div className="cd-preview-play"><IcoPlay /></div>
                <div className="cd-preview-label">Preview this course</div>
              </div>

              {/* Price */}
              <div className="cd-price-section">
                <div className="cd-price-row">
                  <span className="cd-price-now">₹{course.price.toLocaleString("en-IN")}</span>
                  <span className="cd-price-orig">₹{course.original_price.toLocaleString("en-IN")}</span>
                </div>
                <div className="cd-price-save">🔥 Save {savePct}% · Limited Time</div>
              </div>

              {/* Enroll / Go to Course button */}
              <button
                className={`cd-enroll-btn${isEnrolled ? " cd-enroll-btn-enrolled" : ""}`}
                onClick={handleEnrollClick}
              >
                {isEnrolled ? "Go to Course →" : "Enroll Now — ₹" + course.price.toLocaleString("en-IN")}
              </button>

              {!isEnrolled && (
                <p className="cd-enroll-hint">
                  🔐 Login required · 30-day money-back guarantee
                </p>
              )}

              <div className="cd-guarantee"><IcoShield /> 30-day money-back guarantee</div>

              <div className="cd-includes-divider" />
              <div className="cd-includes-title">This course includes:</div>

              {/* What's included — enhanced grid */}
              <div className="cd-includes-grid">
                {[
                  { icon: <IcoVideo />,       label: course.includes.find(i => i.icon==="video")?.text || `${totalLessons} Lessons`,      color:"#0d9e8a", bg:"#e6faf7" },
                  { icon: <IcoClock />,        label: course.includes.find(i => i.icon==="clock")?.text || course.duration_display,         color:"#6366f1", bg:"#eef2ff" },
                  { icon: <IcoCertificate />,  label: "Certificate of completion",                                                           color:"#f59e0b", bg:"#fffbeb" },
                  { icon: <IcoDownload />,     label: "Downloadable resources",                                                              color:"#10b981", bg:"#ecfdf5" },
                  { icon: <IcoZap />,          label: "Lifetime access",                                                                     color:"#ec4899", bg:"#fdf2f8" },
                  { icon: <IcoUsers />,        label: `${course.students_display} enrolled`,                                                 color:"#3b82f6", bg:"#eff6ff" },
                ].map(item => (
                  <div className="cd-inc-tile" key={item.label}>
                    <div className="cd-inc-icon" style={{ color: item.color, background: item.bg }}>
                      {item.icon}
                    </div>
                    <span className="cd-inc-label">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="cd-share-row">
                <button className="cd-share-btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  Share
                </button>
                <button className="cd-wish-btn">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  Wishlist
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ══ LoginModal — same as TestSeriesNormal ══ */}
      {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* ══ TestPriceModal — same as TestSeriesNormal ══ */}
      {showPricingModal && (
        <TestPriceModal
          show={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          discounted_price={String(course.price)}
          original_price={String(course.original_price)}
          blogTitle={course.title}
          test_category_name={course.category}
          onCheckout={() => {
            setShowPricingModal(false);
            setIsEnrolled(true);
          }}
        />
      )}
    </>
  );
};

export default CourseDetail;
